import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  Badge,
  BadgeProps,
  Card,
  CardActionArea,
  CardContent,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';

import { getToken } from 'firebase/messaging';
import { useNavigate } from 'react-router-dom';
import { getAuthState, signOut } from '../../../../store/AuthSlice';
import { deleteFcmToken } from '../../../../store/AuthSlice/V2/AuthApi';
import { useAppSelector } from '../../../../hooks';
import messaging from '../../../../firebase/firebaseConfig';
import { TP_COLORS } from '../../../../constants';
import { getChatState } from '../../../../store/V2/ChatSlice';
import ChatNotifications from '../../Chat/components/ChatNotifications';
import { getAdminChatState } from '../../../../store/V2/AdminChatSlice';
import { getConfigState } from '../../../../store/ConfigSlice';
import {
  removeAuthUserToLocalStorage,
  setAuthUserToLocalStorage,
} from '../../../../helper';

const profileMenuItemStyles: SxProps = {
  borderRadius: '10px',
};

const badgeProps: BadgeProps = {
  overlap: 'circular',
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
  variant: 'dot',
  sx: {
    '& .MuiBadge-badge': {
      boxShadow: '0 0 0 2px #fff',
    },
  },
};

const HeaderUserProfileOptionsViewer: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const authState = useAppSelector(getAuthState);
  const chatState = useAppSelector(getChatState);
  const adminChatState = useAppSelector(getAdminChatState);
  const configState = useAppSelector(getConfigState);

  // State
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { userMapper, user: authUser } = authState;
  const isMenuOpen = Boolean(anchorEl);
  const isChatConnected = chatState.isConnected || adminChatState.isConnected;

  let userAvatar = '';
  let userAvatarFirstLetter = '';
  let userName = '';
  let roleName = '';

  if (authUser) {
    if (authUser.avatar) {
      userAvatar = authUser.avatar;
    }

    if (authUser.firstname) {
      userName = authUser.firstname;
      userAvatarFirstLetter = authUser.firstname.charAt(0);

      if (authUser.lastname) {
        userName += ` ${authUser.lastname}`;
      }
    }

    if (authUser.role && authUser.role.name) {
      roleName = authUser.role.name;
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const signOutUser = async () => {
    getToken(messaging).then((token) => {
      dispatch(deleteFcmToken(token));
    });

    dispatch(signOut());

    closeMenu();
  };

  const navigateToProfile = () => {
    navigate(`${process.env.PUBLIC_URL}/portal/profile`);

    closeMenu();
  };

  const handleAccountChange = (userId: string) => {
    const selectedUser = userMapper?.find(
      (userItem) => userItem._id === userId
    );
    if (selectedUser) {
      removeAuthUserToLocalStorage();
      setAuthUserToLocalStorage(selectedUser);
      window.location.reload();
    }

    closeMenu();
  };

  return (
    <>
      {!configState.fromIGCSEBuddy ? <ChatNotifications /> : null}
      <Card elevation={0}>
        <CardActionArea
          id="profile-menu-button"
          aria-controls={isMenuOpen ? 'profile-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
          onClick={openMenu}
        >
          <CardContent sx={{ p: '3px' }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Badge
                {...badgeProps}
                invisible={configState.fromIGCSEBuddy}
                color={isChatConnected ? 'success' : 'warning'}
              >
                {userAvatar ? (
                  <Avatar
                    src={userAvatar}
                    sx={{ width: '35px', height: '35px' }}
                  />
                ) : (
                  <Avatar sx={{ width: '35px', height: '35px' }}>
                    {userAvatarFirstLetter}
                  </Avatar>
                )}
              </Badge>
              {!isXsScreen ? (
                <Stack>
                  <Typography
                    fontSize="14px"
                    fontWeight={600}
                    maxWidth="125px"
                    className="ellipsis-text"
                  >
                    {userName}
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontWeight={400}
                    maxWidth="125px"
                    className="ellipsis-text"
                    color="#94A3B8"
                    mt="-3px"
                  >
                    {roleName}
                  </Typography>
                </Stack>
              ) : null}
              {!isXsScreen ? (
                <>
                  {isMenuOpen ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </>
              ) : null}
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
      {/* MODALS */}
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'profile-menu-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { minWidth: '245px', p: '15px', borderRadius: '20px' },
        }}
      >
        {!configState.fromIGCSEBuddy &&
          (userMapper && userMapper.length > 1
            ? userMapper
                ?.slice()
                .sort((a, b) => {
                  const aIsSelected = authUser?._id === a._id;
                  const bIsSelected = authUser?._id === b._id;
                  if (aIsSelected && !bIsSelected) return -1;
                  if (!aIsSelected && bIsSelected) return 1;
                  return 0;
                })
                .map((userItem) => {
                  const fullName = `${userItem.firstname} ${
                    userItem.lastname || ''
                  }`;
                  const isSelected = authUser?._id === userItem._id;
                  return (
                    <MenuItem
                      key={userItem._id}
                      selected={isSelected}
                      onClick={() =>
                        isSelected
                          ? navigateToProfile()
                          : handleAccountChange(userItem._id)
                      }
                      sx={{
                        ...profileMenuItemStyles,
                        padding: '10px 12px',
                        backgroundColor: '#E2E8F04D',
                        marginBottom: '5px',
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        {userItem.avatar ? (
                          <Avatar
                            src={userItem.avatar}
                            sx={{ width: '35px', height: '35px' }}
                          />
                        ) : (
                          <Avatar sx={{ width: '35px', height: '35px' }}>
                            {userItem.firstname.charAt(0)}
                          </Avatar>
                        )}
                        <Stack>
                          <Typography
                            maxWidth="160px"
                            className="ellipsis-text"
                            color={TP_COLORS.gray.darken}
                          >
                            {fullName}
                          </Typography>
                          <Typography fontSize="12px" color="#647488" mt="-3px">
                            {userItem.role.name}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  );
                })
            : [
                <MenuItem
                  key="single-user"
                  onClick={navigateToProfile}
                  sx={{
                    ...profileMenuItemStyles,
                    padding: '10px 12px',
                    backgroundColor: '#E2E8F04D',
                    marginBottom: '5px',
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    {userAvatar ? (
                      <Avatar
                        src={userAvatar}
                        sx={{ width: '35px', height: '35px' }}
                      />
                    ) : (
                      <Avatar sx={{ width: '35px', height: '35px' }}>
                        {userAvatarFirstLetter}
                      </Avatar>
                    )}
                    <Stack>
                      <Typography
                        maxWidth="160px"
                        className="ellipsis-text"
                        color={TP_COLORS.gray.darken}
                      >
                        {userName}
                      </Typography>
                      <Typography fontSize="12px" color="#647488" mt="-3px">
                        See Profile
                      </Typography>
                    </Stack>
                  </Stack>
                </MenuItem>,
              ])}
        <MenuItem
          onClick={signOutUser}
          sx={{
            ...profileMenuItemStyles,
            height: '50px',
            justifyContent: 'center',
            backgroundColor: 'error.light',
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'error.main',
            },
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};

export default HeaderUserProfileOptionsViewer;
